import { useState, useEffect } from "react";
import { Calendar, Radio, Plus, X, Repeat, Loader2, CheckCircle, Trash2, Clock } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const API = BACKEND_URL;

const RECURRENCES = [
  { value: "none", label: "One time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export const BroadcastScheduler = ({ authToken }) => {
  const [schedules, setSchedules] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    podcast_episode_id: "",
    station_ids: [],
    scheduled_at: "",
    recurrence: "none",
    title: "",
    notes: "",
  });

  const headers = { Authorization: `Bearer ${authToken}` };
  const jheaders = { ...headers, "Content-Type": "application/json" };

  const load = async () => {
    setLoading(true);
    try {
      const [sch, lib, dir] = await Promise.all([
        fetch(`${API}/api/ai/broadcast/schedules`, { headers }).then((r) => r.json()),
        fetch(`${API}/api/ai/podcast/library`, { headers }).then((r) => r.json()).catch(() => []),
        fetch(`${API}/api/directory?limit=100`).then((r) => r.json()).catch(() => ({ listings: [] })),
      ]);
      setSchedules(Array.isArray(sch) ? sch : []);
      setEpisodes(Array.isArray(lib) ? lib : (lib?.episodes || []));
      setStations((dir?.listings || []).filter((l) => l.status === "approved"));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleStation = (id) => {
    setForm((f) => ({ ...f, station_ids: f.station_ids.includes(id) ? f.station_ids.filter((x) => x !== id) : [...f.station_ids, id] }));
  };

  const save = async () => {
    setError("");
    if (!form.podcast_episode_id) return setError("Select a podcast episode");
    if (!form.station_ids.length) return setError("Select at least one station");
    if (!form.scheduled_at) return setError("Choose a date/time");
    setSaving(true);
    try {
      const iso = new Date(form.scheduled_at).toISOString();
      const r = await fetch(`${API}/api/ai/broadcast/schedules`, {
        method: "POST",
        headers: jheaders,
        body: JSON.stringify({ ...form, scheduled_at: iso }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to schedule");
      setShowForm(false);
      setForm({ podcast_episode_id: "", station_ids: [], scheduled_at: "", recurrence: "none", title: "", notes: "" });
      await load();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this broadcast?")) return;
    await fetch(`${API}/api/ai/broadcast/schedules/${id}`, { method: "DELETE", headers });
    await load();
  };

  return (
    <div data-testid="broadcast-scheduler">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-400" /> Broadcast Scheduler
        </h3>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-400 px-3 py-1.5 rounded-lg text-sm border border-cyan-500/20 transition"
          data-testid="scheduler-new">
          <Plus className="w-4 h-4" /> New broadcast
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto" /></div>
      ) : schedules.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-12 text-center">
          <Calendar className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/40 text-sm">No broadcasts scheduled yet.</p>
          <p className="text-white/25 text-xs mt-1">Generate a podcast, then schedule it here.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="scheduler-list">
          {schedules.map((s) => (
            <div key={s.schedule_id} className="bg-white/[0.03] border border-white/5 rounded-xl p-4 flex items-center gap-4">
              <div className={`w-2 h-2 rounded-full ${
                s.status === "cancelled" ? "bg-white/20" : s.status === "executed" ? "bg-emerald-400" : "bg-cyan-400 animate-pulse"
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{s.title || "Untitled broadcast"}</p>
                <div className="flex items-center gap-3 text-white/40 text-xs mt-0.5 flex-wrap">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(s.scheduled_at).toLocaleString()}</span>
                  {s.recurrence !== "none" && <span className="flex items-center gap-1"><Repeat className="w-3 h-3" /> {s.recurrence}</span>}
                  <span className="flex items-center gap-1"><Radio className="w-3 h-3" /> {(s.station_ids || []).length} station{s.station_ids?.length === 1 ? "" : "s"}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                    s.status === "cancelled" ? "bg-white/5 text-white/40" : s.status === "executed" ? "bg-emerald-500/15 text-emerald-400" : "bg-cyan-500/15 text-cyan-400"
                  }`}>{s.status}</span>
                </div>
              </div>
              {s.status !== "cancelled" && (
                <button onClick={() => cancel(s.schedule_id)}
                  className="text-white/30 hover:text-red-400 transition"
                  data-testid={`scheduler-cancel-${s.schedule_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="scheduler-modal">
          <div className="bg-gray-950 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-950 border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <h4 className="text-white font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-400" /> Schedule a broadcast</h4>
              <button onClick={() => setShowForm(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-white/50 text-xs block mb-1.5">Podcast episode</label>
                <select value={form.podcast_episode_id} onChange={(e) => setForm({ ...form, podcast_episode_id: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                  data-testid="scheduler-episode-select">
                  <option value="">— Choose an episode —</option>
                  {episodes.map((ep) => (
                    <option key={ep.episode_id || ep.job_id} value={ep.episode_id || ep.job_id}>
                      {ep.title || ep.podcast_title || "Untitled"}
                    </option>
                  ))}
                </select>
                {episodes.length === 0 && <p className="text-white/30 text-xs mt-1">No episodes yet — generate one in the Podcast Generator tab first.</p>}
              </div>

              <div>
                <label className="text-white/50 text-xs block mb-1.5">Stations ({form.station_ids.length} selected)</label>
                <div className="max-h-40 overflow-y-auto bg-white/[0.02] border border-white/5 rounded-lg p-2 space-y-1">
                  {stations.length === 0 && <p className="text-white/30 text-xs px-2 py-2">No approved stations found.</p>}
                  {stations.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer">
                      <input type="checkbox" checked={form.station_ids.includes(s.id)} onChange={() => toggleStation(s.id)}
                        className="accent-cyan-500" data-testid={`scheduler-station-${s.id}`} />
                      <span className="text-white/80 text-sm">{s.station_name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs block mb-1.5">Date & time</label>
                  <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                    data-testid="scheduler-datetime" />
                </div>
                <div>
                  <label className="text-white/50 text-xs block mb-1.5">Recurrence</label>
                  <select value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                    data-testid="scheduler-recurrence">
                    {RECURRENCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs block mb-1.5">Title</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Optional — e.g. Morning drive intro"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}
            </div>
            <div className="sticky bottom-0 bg-gray-950 border-t border-white/10 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white text-sm">Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                data-testid="scheduler-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Schedule broadcast
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastScheduler;
